import { Box ,Container ,Grid, Typography} from "@mui/material";
import { motion, useInView } from 'framer-motion';
import { useRef } from "react";
import { alpha } from '@mui/material/styles';
import { P, } from '../theme/palette';
import { useTenantConfig } from "../hooks/useTenant";

const RedTheme = {
  main: '#b91010ff',
  light: '#d33434ff',
  dark: '#960505ff',
};

const VISITOR_FEATURES = [
  { img: '/assets/icon/chart.webp', text: 'Güncel bağış tutarlarını görür', gradient: 'linear-gradient(135deg, #b91010ff 0%, #960505ff 100%)' },
  { img: '/assets/icon/heart2.webp', text: 'Aktif projelere bağış yapabilir', gradient: 'linear-gradient(135deg, #d33434ff 0%, #b91010ff 100%)' },
  { img: '/assets/icon/experience.webp', text: 'Kategoriler arasında gezebilir', gradient: 'linear-gradient(135deg, #960505ff 0%, #7a0404ff 100%)' },
  { img: '/assets/icon/mobiles-phones.webp', text: 'Mobil ve masaüstünde sorunsuz deneyim yaşar', gradient: 'linear-gradient(135deg, #d33434ff 0%, #960505ff 100%)' },
];


const slideLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 },
  }),
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

const VisitorExperience = ({ isMobile }) => {
  const {tenantName, siteUrl} = useTenantConfig();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <Box ref={ref} sx={{ 
      py: { xs: 8, md: 14 }, 
      background: "#fff",
      borderBottom: '1px solid #e5e7eb',
    }}>
      <Container maxWidth="lg">
        <Grid container spacing={8} alignItems="center">
          {/* Sol: Text */}
          <Grid item xs={12} md={6}>
            <motion.div variants={slideLeft} initial="hidden" animate={isInView ? 'visible' : 'hidden'}>


              <Typography variant="h3" sx={{
                fontWeight: 900, fontSize: isMobile ? '1.8rem' : '2.3rem',
                color: P.neutral[900], lineHeight: 1.15, mb: 3,
              }}>
                Ziyaretçileriniz İçin{' '}
                <Box component="span" sx={{
                  background: `linear-gradient(135deg, ${RedTheme.main} 0%, ${RedTheme.dark} 100%)`,
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                }}>
                  Her Şey Otomatik
                </Box>
              </Typography>

              <Typography sx={{
                color: P.neutral[500], fontSize: '1.08rem', lineHeight: 1.8, mb: 4,
              }}>
                {tenantName} widget sistemi sayesinde sitenizdeki içerikler otomatik olarak güncellenir ve ziyaretçileriniz her zaman en güncel bilgileri görür.
              </Typography>

              {/* Ziyaretçi özellikleri */}
              <Grid container spacing={2.5}>
                {VISITOR_FEATURES.map((f, i) => {
                  return (
                    <Grid item xs={12} key={i}>
                      <motion.div
                        variants={fadeUp}
                        custom={i}
                        initial="hidden"
                        animate={isInView ? 'visible' : 'hidden'}
                        style={{ height: '100%' }}
                      >
                        <Box sx={{
                          p:1,
                          borderRadius: 2.5,
                          height: '100%',
                          background: '#ffffff',
                          border: '1px solid #e5e7eb',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          cursor: 'pointer',
                          '&:hover': {
                            background: '#fff',
                            borderColor: RedTheme.main,
                          },
                        }}>
                          <Box className="feature-icon-box" sx={{
                            width: 56,
                            height: 56,
                            flexShrink: 0,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: f.gradient,
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}>
                            <Box
                              component="img"
                              src={f.img}
                              alt={f.text}
                              sx={{
                                width: 32,
                                height: 32,
                                objectFit: 'contain',
                                filter: 'brightness(0) invert(1)',
                              }}
                            />
                          </Box>
                          <Typography sx={{
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            color: '#0f172a',
                            lineHeight: 1.4,
                          }}>
                            {f.text}
                          </Typography>
                        </Box>
                      </motion.div>
                    </Grid>
                  );
                })}
              </Grid>
            </motion.div>
          </Grid>

          {/* Sağ: Browser Frame Mockup */}
          <Grid item xs={12} md={6}>
            <motion.div variants={scaleIn} initial="hidden" animate={isInView ? 'visible' : 'hidden'}>
              <Box sx={{
                borderRadius: '12px 12px 12px 12px',
                overflow: 'hidden',
                boxShadow: '0 25px 60px rgba(0,0,0,0.12), 0 8px 20px rgba(0,0,0,0.06)',
                border: '1px solid #d4d4d8',
              }}>


                {/* Address Bar */}
                <Box sx={{
                  px: 1.5, py: 0.8,
                  background: '#fff',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex', alignItems: 'center', gap: 1,
                }}>
                  <Box sx={{ display: 'flex', gap: 0.5, color: '#bbb', fontSize: '0.7rem' }}>
                    <span>◀</span><span>▶</span>
                  </Box>
                  <Box sx={{
                    flex: 1, px: 1.5, py: 0.5, borderRadius: '8px',
                    background: '#f4f4f5', border: '1px solid #e5e7eb',
                    fontSize: '0.7rem', color: '#71717a',
                    display: 'flex', alignItems: 'center', gap: 0.5,
                  }}>
                     {siteUrl}
                  </Box>
                </Box>

                {/* Site İçeriği */}
                <Box sx={{ background: '#fff' }}>
                  {/* Mini Navbar */}
                  <Box sx={{
                    px: 2, py: 1.2,
                    borderBottom: '1px solid #f1f5f9',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Box sx={{ width: 22, height: 22, borderRadius: 1, background: `linear-gradient(135deg, ${RedTheme.main}, ${RedTheme.dark})` }} />
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: '#1e293b' }}>ÖrnekDernek</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      {['Anasayfa', 'Projeler', 'Bağış', 'İletişim'].map((m, i) => (
                        <Typography key={i} sx={{ fontSize: '0.55rem', color: i === 0 ? RedTheme.main : '#64748b', fontWeight: i === 0 ? 700 : 500, cursor: 'pointer' }}>{m}</Typography>
                      ))}
                    </Box>
                  </Box>

                  {/* Hero */}
                  <Box sx={{
                    px: 2, py: 2.5,
                    background: `linear-gradient(160deg, ${alpha(RedTheme.main, 0.04)} 0%, #fff 60%)`,
                  }}>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2, mb: 0.5 }}>
                      Birlikte Güzel
                    </Typography>
                    <Typography sx={{ fontSize: '0.55rem', color: '#64748b', mb: 1.5, lineHeight: 1.5 }}>
                      Yardımlarınızla hayatları değiştiriyoruz.
                    </Typography>
                    <Box sx={{
                      display: 'inline-flex', px: 1.5, py: 0.5, borderRadius: 1,
                      background: `linear-gradient(135deg, ${RedTheme.main}, ${RedTheme.dark})`,
                    }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#fff', fontWeight: 700 }}>Bağış Yap</Typography>
                    </Box>
                  </Box>

                  {/* Proje Kartları */}
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#1e293b', mb: 1 }}>Aktif Projeler</Typography>
                    <Grid container spacing={1}>
                      {[
                        { name: 'Eğitim Desteği', pct: 72, color: RedTheme.main },
                        { name: 'Gıda Yardımı', pct: 45, color: RedTheme.light },
                        { name: 'Sağlık Projesi', pct: 88, color: RedTheme.dark },
                      ].map((proj, i) => (
                        <Grid item xs={4} key={i}>
                          <Box sx={{
                            p: 1, borderRadius: 1.5,
                            border: '1px solid #f1f5f9',
                            background: '#fafbfc',
                          }}>
                            <Box sx={{
                              width: '100%', height: 32, borderRadius: 1, mb: 0.8,
                              background: `linear-gradient(135deg, ${alpha(proj.color, 0.1)} 0%, ${alpha(proj.color, 0.05)} 100%)`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: proj.color }}>
                                %{proj.pct}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.5rem', fontWeight: 700, color: '#334155', mb: 0.3 }}>{proj.name}</Typography>
                            {/* Progress bar */}
                            <Box sx={{ width: '100%', height: 3, borderRadius: 4, bgcolor: '#f1f5f9' }}>
                              <Box sx={{ width: `${proj.pct}%`, height: '100%', borderRadius: 4, bgcolor: proj.color, transition: 'width 1s ease' }} />
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  {/* İstatistik Bandı */}
                  <Box sx={{
                    mx: 2, mb: 1.5, p: 1.2, borderRadius: 1.5,
                    background: alpha(RedTheme.main, 0.04),
                    border: `1px solid ${alpha(RedTheme.main, 0.08)}`,
                    display: 'flex', justifyContent: 'space-around',
                  }}>
                    {[
                      { val: '1.2K', label: 'Bağışçı' },
                      { val: '₺284K', label: 'Toplanan' },
                      { val: '18', label: 'Proje' },
                    ].map((s, i) => (
                      <Box key={i} sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: RedTheme.main }}>{s.val}</Typography>
                        <Typography sx={{ fontSize: '0.45rem', color: '#94a3b8', fontWeight: 500 }}>{s.label}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Canlı güncelleme badge */}
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Box sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.8,
                      px: 1.5, py: 0.5, borderRadius: 50,
                      background: alpha(RedTheme.main, 0.06),
                      border: `1px solid ${alpha(RedTheme.main, 0.12)}`,
                    }}>
                      <Box sx={{
                        width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e',
                        boxShadow: '0 0 6px rgba(34,197,94,0.5)',
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                          '50%': { opacity: 0.5, transform: 'scale(0.8)' },
                        },
                      }} />
                      <Typography sx={{ fontSize: '0.5rem', fontWeight: 600, color: '#64748b' }}>
                        Canlı • Veriler otomatik güncelleniyor
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default VisitorExperience;